import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Sun, Moon, Monitor, Bell, Webhook, Users, Lock, Trash2, Plus,
  TestTube2, Building2, Palette, ShieldCheck, Info, Copy, Timer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  PageHeader, SectionCard, Badge, IconButton, ErrorState, SeverityBadge,
  LoadingState, EmptyState, useConfirm, describeError,
} from '@/components/primitives';
import { useTheme } from '@/context/ThemeContext';
import OrgForm from '@/components/org/OrgForm';
import { SECTIONS, fieldErrors, fromProfile, sectionProgress, toPayload } from '@/components/org/orgModel';
import { DEFAULT_SLA_HOURS, setSlaPolicyCache } from '@/lib/sla';
import '@/styles/workspace.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const EVENT_OPTIONS = [
  { key: 'critical_threat', label: 'Critical' },
  { key: 'high_threat', label: 'High' },
  { key: 'medium_threat', label: 'Medium' },
  { key: 'all', label: 'Everything' },
];
const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * One GET per resource, keyed only by its path (never by an object created
 * during render, which is what made the previous page re-fetch forever).
 * Failures are kept as an error state so they are never shown as empty data.
 */
function useResource(path, select = (d) => d) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const selectRef = React.useRef(select);
  selectRef.current = select;

  const load = useCallback(async () => {
    setState((s) => (s.data ? s : { status: 'loading', data: null, error: null }));
    try {
      const res = await axios.get(`${API}${path}`, { headers: authHeaders() });
      setState({ status: 'ready', data: selectRef.current(res.data), error: null });
    } catch (error) {
      setState((s) => ({ ...s, status: 'error', error }));
    }
  }, [path]);

  useEffect(() => { load(); }, [load]);

  const setData = useCallback((next) => {
    setState((s) => ({ ...s, data: typeof next === 'function' ? next(s.data) : next }));
  }, []);

  return { ...state, reload: load, setData };
}

function Resource({ res, children, label }) {
  if (res.status === 'loading') return <LoadingState label={label} rows={3} />;
  if (res.status === 'error' && !res.data) {
    return <ErrorState title={`Couldn't load ${label}`} error={res.error} onRetry={res.reload} />;
  }
  return children(res.data);
}

// Webhook URLs usually embed a secret token: show the host and only the last 4 characters.
function maskUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/$/, '');
    return path.length > 1 ? `${u.protocol}//${u.host}/…${path.slice(-4)}` : `${u.protocol}//${u.host}`;
  } catch {
    return 'Invalid URL';
  }
}

/* ------------------------------------------------------------------ Profile */
function ProfileSection() {
  const profile = useResource('/profile');
  const options = useResource('/organization/options');
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (profile.data) setForm(fromProfile(profile.data)); }, [profile.data]);

  const payload = useMemo(() => (form ? toPayload(form, { includeInternal: false }) : null), [form]);
  const dirty = useMemo(() => {
    if (!form || !profile.data) return false;
    return JSON.stringify(payload) !== JSON.stringify(toPayload(fromProfile(profile.data), { includeInternal: false }));
  }, [form, payload, profile.data]);

  const completeness = useMemo(() => {
    if (!form) return null;
    let filled = 0;
    let total = 0;
    SECTIONS.forEach((s) => {
      const p = sectionProgress(s, form.insights[s.key]);
      filled += p.filled;
      total += p.total;
    });
    return { filled, total, percent: total ? Math.round((100 * filled) / total) : 0 };
  }, [form]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      await axios.put(`${API}/profile`, payload, { headers: authHeaders() });
      profile.setData((p) => ({ ...p, ...payload }));
      toast.success('Profile saved', { description: 'Your Inbox re-matches stories to the new details.' });
    } catch (err) {
      const fe = fieldErrors(err);
      if (Object.keys(fe).length) {
        setErrors(fe);
        toast.error('Some fields need attention', { description: 'They are marked in the form.' });
      } else {
        toast.error("Couldn't save the profile", { description: describeError(err) });
      }
    } finally {
      setSaving(false);
    }
  };

  const loading = profile.status === 'loading' || options.status === 'loading';
  const failed = (profile.status === 'error' && !profile.data) || (options.status === 'error' && !options.data);

  return (
    <SectionCard title="Organization profile" subtitle="The more specific this is, the better your briefs match. Stories that name a product or vendor you run are matched to you automatically." icon={Building2}>
      {loading ? <LoadingState label="your profile" rows={4} />
        : failed ? <ErrorState title="Couldn't load your profile" error={profile.error || options.error} onRetry={() => { profile.reload(); options.reload(); }} />
          : form && (
            <form onSubmit={save} className="ws-stack" noValidate>
              {completeness && (
                <div className="ws-note" role="status">
                  <Info aria-hidden="true" />
                  <span><strong>{completeness.percent}% of the detail fields are filled in</strong> ({completeness.filled} of {completeness.total}). Open a section below to add more.</span>
                </div>
              )}
              <OrgForm form={form} onChange={setForm} options={options.data || {}} errors={errors} />
              <div className="ws-form-actions">
                <Button type="button" variant="ghost" disabled={!dirty || saving} onClick={() => { setForm(fromProfile(profile.data)); setErrors({}); }}>Discard</Button>
                <Button type="submit" disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save profile'}</Button>
              </div>
            </form>
          )}
    </SectionCard>
  );
}

/* --------------------------------------------------------------- Appearance */
function AppearanceSection() {
  const { preference, setTheme } = useTheme();
  return (
    <SectionCard title="Appearance" subtitle="System follows your operating system and changes with it." icon={Palette}>
      <RadioGroup value={preference} onValueChange={setTheme} aria-label="Colour theme" className="ws-theme-options">
        {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
          <label key={value} className="ws-theme-option" data-state={preference === value ? 'checked' : 'unchecked'}>
            <RadioGroupItem value={value} className="sr-only" aria-label={label} />
            <Icon aria-hidden="true" />
            {label}
            <span className="ws-theme-dot" aria-hidden="true" />
          </label>
        ))}
      </RadioGroup>
    </SectionCard>
  );
}

/* ------------------------------------------------------------ Notifications */
function NotificationsSection() {
  const prefs = useResource('/user/preferences');

  const update = async (patch) => {
    const before = prefs.data;
    prefs.setData((p) => ({ ...p, ...patch }));
    try {
      await axios.put(`${API}/user/preferences`, patch, { headers: authHeaders() });
    } catch (err) {
      prefs.setData(before);
      toast.error("Couldn't save the preference", { description: describeError(err) });
    }
  };

  return (
    <SectionCard title="Notifications" subtitle="How IntelliSecure tells you about new threats." icon={Bell}>
      <Resource res={prefs} label="notification settings">
        {(p) => (
          <div className="ws-stack">
            <div className="ws-row">
              <div className="ws-row-main">
                <Bell aria-hidden="true" />
                <div>
                  <div className="ws-row-title" id="np-inapp">In-app notifications</div>
                  <div className="ws-row-desc">The bell in the top bar and live updates while you work.</div>
                </div>
              </div>
              <Switch aria-labelledby="np-inapp" checked={p.notifications_enabled !== false}
                      onCheckedChange={(v) => update({ notifications_enabled: v })} />
            </div>
            <div className="ws-note">
              <Info aria-hidden="true" />
              <span>
                <strong>Email alerts are not available yet.</strong> This deployment has no email delivery configured, so
                instant, daily and weekly emails are not sent. Use a <strong>webhook</strong> to push critical threats to Slack, Teams or PagerDuty.
              </span>
            </div>
          </div>
        )}
      </Resource>
    </SectionCard>
  );
}

/* ----------------------------------------------------------------- Webhooks */
function WebhooksSection() {
  const hooks = useResource('/user/webhooks', (d) => d.webhooks || []);
  const [draft, setDraft] = useState({ name: '', url: '', events: ['critical_threat'] });
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const add = async (e) => {
    e.preventDefault();
    if (!/^https:\/\//i.test(draft.url.trim())) { toast.error('The URL must start with https://'); return; }
    if (!draft.events.length) { toast.error('Pick at least one event'); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API}/user/webhooks`, { ...draft, url: draft.url.trim() }, { headers: authHeaders() });
      hooks.setData((h) => [...(h || []), res.data.webhook]);
      setDraft({ name: '', url: '', events: ['critical_threat'] });
      toast.success('Webhook added');
    } catch (err) {
      toast.error("Couldn't add the webhook", { description: describeError(err) });
    } finally {
      setAdding(false);
    }
  };

  const remove = async (wh) => {
    const ok = await confirm({
      title: `Delete “${wh.name || 'Webhook'}”?`,
      description: 'Threat notifications will stop going to this endpoint. This cannot be undone.',
      confirmLabel: 'Delete webhook',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/user/webhooks/${wh.id}`, { headers: authHeaders() });
      hooks.setData((h) => h.filter((x) => x.id !== wh.id));
      toast.success('Webhook deleted');
    } catch (err) {
      toast.error("Couldn't delete the webhook", { description: describeError(err) });
    }
  };

  const test = async (wh) => {
    setTesting(wh.id);
    try {
      const res = await axios.post(`${API}/user/webhooks/${wh.id}/test`, {}, { headers: authHeaders() });
      if (res.data.success) toast.success('Test delivered', { description: `The endpoint answered HTTP ${res.data.status_code}.` });
      else toast.error('Test failed', { description: res.data.error || `The endpoint answered HTTP ${res.data.status_code}.` });
    } catch (err) {
      toast.error('Test failed', { description: describeError(err) });
    } finally {
      setTesting(null);
    }
  };

  const toggleEvent = (key) => setDraft((d) => ({
    ...d,
    events: d.events.includes(key) ? d.events.filter((e) => e !== key) : [...d.events, key],
  }));

  return (
    <SectionCard title="Webhooks" subtitle="Push threat notifications to Slack, Teams, PagerDuty or any HTTPS endpoint." icon={Webhook}>
      <div className="ws-stack">
        <form className="ws-stack" onSubmit={add} noValidate>
          <div className="ws-form-grid">
            <div className="ws-field">
              <label className="ws-label" htmlFor="wh-name">Name</label>
              <Input id="wh-name" className="ws-input-inset" placeholder="Security channel" value={draft.name}
                     onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
            </div>
            <div className="ws-field">
              <label className="ws-label" htmlFor="wh-url">Endpoint URL</label>
              <Input id="wh-url" type="url" className="ws-input-inset" placeholder="https://hooks.example.com/…" value={draft.url}
                     autoComplete="off" onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))} />
            </div>
          </div>
          <div className="ws-field">
            <span className="ws-label" id="wh-events-label">Send when severity is</span>
            <div className="ws-chips" role="group" aria-labelledby="wh-events-label">
              {EVENT_OPTIONS.map((ev) => (
                <button key={ev.key} type="button" className="ws-chip" aria-pressed={draft.events.includes(ev.key)} onClick={() => toggleEvent(ev.key)}>
                  {ev.label}
                </button>
              ))}
            </div>
            <span className="ws-hint">Only public HTTPS endpoints are accepted. The URL often contains a secret, so it is masked after saving.</span>
          </div>
          <div className="ws-form-actions">
            <Button type="submit" disabled={adding || !draft.url.trim()}><Plus aria-hidden="true" /> {adding ? 'Adding…' : 'Add webhook'}</Button>
          </div>
        </form>

        <Resource res={hooks} label="webhooks">
          {(list) => list.length === 0 ? (
            <EmptyState variant="compact" icon={Webhook} title="No webhooks yet" description="Add one above to receive threat notifications outside the app." />
          ) : (
            <div role="list">
              {list.map((wh) => (
                <div key={wh.id} className="ws-row" role="listitem">
                  <div className="ws-row-main">
                    <Webhook aria-hidden="true" />
                    <div>
                      <div className="ws-row-title">{wh.name || 'Webhook'} {wh.enabled === false && <Badge size="sm">Disabled</Badge>}</div>
                      <div className="ws-webhook-url" title="Masked: the full URL is not shown again">{maskUrl(wh.url)}</div>
                      <div className="ws-chips" style={{ marginTop: 6 }}>
                        {(wh.events || []).map((ev) => (
                          <Badge key={ev} size="sm" plain>{EVENT_OPTIONS.find((o) => o.key === ev)?.label || ev}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="ws-row-actions">
                    <Button variant="outline" size="sm" disabled={testing === wh.id} onClick={() => test(wh)}>
                      <TestTube2 aria-hidden="true" /> {testing === wh.id ? 'Sending…' : 'Send test'}
                    </Button>
                    <IconButton label={`Delete webhook ${wh.name || ''}`.trim()} icon={Trash2} variant="danger" onClick={() => remove(wh)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Resource>
      </div>
      {confirmDialog}
    </SectionCard>
  );
}

/* ----------------------------------------------------------------- Security */
function SecuritySection() {
  const status = useResource('/auth/mfa/status');
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [secretShown, setSecretShown] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/mfa/setup`, {}, { headers: authHeaders() });
      setSetup(res.data); setCode(''); setSecretShown(false);
    } catch (err) {
      toast.error("Couldn't start MFA setup", { description: describeError(err) });
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/verify`, { code: code.trim() }, { headers: authHeaders() });
      status.setData({ mfa_enabled: true });
      setSetup(null);
      toast.success('Two-factor authentication is on');
    } catch (err) {
      toast.error('That code did not work', { description: 'Check the 6-digit code in your authenticator app and try again.' });
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/mfa/disable`, { code: disableCode.trim() }, { headers: authHeaders() });
      status.setData({ mfa_enabled: false });
      setDisableOpen(false); setDisableCode('');
      toast.success('Two-factor authentication is off');
    } catch (err) {
      toast.error('That code did not work', { description: 'Enter the current 6-digit code from your authenticator app.' });
    } finally { setBusy(false); }
  };

  const copySecret = async () => {
    try { await navigator.clipboard.writeText(setup.secret); toast.success('Key copied'); } catch { toast.error("Couldn't copy"); }
  };

  return (
    <SectionCard title="Two-factor authentication" subtitle="Require a code from an authenticator app when you sign in." icon={Lock}>
      <Resource res={status} label="your security settings">
        {(s) => (
          <div className="ws-row">
            <div className="ws-row-main">
              <ShieldCheck aria-hidden="true" style={{ color: s.mfa_enabled ? 'var(--state-success)' : undefined }} />
              <div>
                <div className="ws-row-title">Authenticator app (TOTP)</div>
                <div className="ws-row-desc">{s.mfa_enabled ? 'On. Your account asks for a code at every sign-in.' : 'Off. Anyone with your password can sign in.'}</div>
              </div>
            </div>
            {s.mfa_enabled
              ? <Button variant="outline" size="sm" onClick={() => setDisableOpen(true)}>Turn off</Button>
              : <Button size="sm" onClick={start} disabled={busy}>Set up</Button>}
          </div>
        )}
      </Resource>

      <Dialog open={Boolean(setup)} onOpenChange={(o) => { if (!o) setSetup(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set up two-factor authentication</DialogTitle>
            <DialogDescription>Scan the QR code with Google Authenticator, Authy, 1Password or similar, then enter the 6-digit code it shows.</DialogDescription>
          </DialogHeader>
          {setup && (
            <form className="ws-stack" onSubmit={verify}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img src={setup.qr_code} alt="QR code to add IntelliSecure to your authenticator app" className="ws-qr" style={{ background: '#fff', padding: 8 }} />
              </div>
              <div className="ws-field" style={{ alignItems: 'center' }}>
                <span className="ws-hint">Can't scan? Enter this key manually.</span>
                {secretShown ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <code className="ws-secret">{setup.secret}</code>
                    <IconButton label="Copy setup key" icon={Copy} size="sm" onClick={copySecret} />
                  </span>
                ) : (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setSecretShown(true)}>Show setup key</Button>
                )}
              </div>
              <div className="ws-field">
                <label className="ws-label" htmlFor="mfa-code">6-digit code</label>
                <Input id="mfa-code" className="ws-input-inset ws-mono" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*"
                       maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} autoFocus />
              </div>
              <div className="ws-form-actions">
                <Button type="button" variant="ghost" onClick={() => setSetup(null)}>Cancel</Button>
                <Button type="submit" disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Turn on'}</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off two-factor authentication?</DialogTitle>
            <DialogDescription>Enter the current code from your authenticator app to confirm it is you.</DialogDescription>
          </DialogHeader>
          <form className="ws-stack" onSubmit={disable}>
            <div className="ws-field">
              <label className="ws-label" htmlFor="mfa-off-code">6-digit code</label>
              <Input id="mfa-off-code" className="ws-input-inset ws-mono" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                     placeholder="000000" value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))} autoFocus />
            </div>
            <div className="ws-form-actions">
              <Button type="button" variant="ghost" onClick={() => setDisableOpen(false)}>Cancel</Button>
              <Button type="submit" variant="destructive" disabled={busy || disableCode.length !== 6}>{busy ? 'Turning off…' : 'Turn off'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

/* ------------------------------------------------------------- Organization */
function OrganizationSection() {
  const org = useResource('/organization/members');
  const [confirm, confirmDialog] = useConfirm();

  const changeRole = async (member, role) => {
    if (role === member.role) return;
    const ok = await confirm({
      title: `Change ${member.email} to ${role}?`,
      description: role === 'admin' ? 'Admins can manage members and organization settings.' : 'Their permissions change immediately.',
      confirmLabel: 'Change role',
    });
    if (!ok) return;
    try {
      await axios.put(`${API}/organization/members/${member.user_id}/role`, { role }, { headers: authHeaders() });
      org.setData((o) => ({ ...o, members: o.members.map((m) => (m.user_id === member.user_id ? { ...m, role } : m)) }));
      toast.success('Role updated');
    } catch (err) {
      toast.error("Couldn't change the role", { description: describeError(err) });
    }
  };

  return (
    <SectionCard title="Organization" subtitle="People in your organization and what they can do." icon={Users}>
      <Resource res={org} label="organization members">
        {(o) => (
          <div className="ws-stack">
            <div className="ws-meta">Company: <strong style={{ color: 'var(--text-primary)' }}>{o.company || 'Not set'}</strong></div>
            {o.members.length <= 1 && (
              <div className="ws-note">
                <Info aria-hidden="true" />
                <span>You are the only member. Inviting teammates and shared organizations are not available yet; each account currently has its own workspace.</span>
              </div>
            )}
            <div role="list">
              {o.members.map((m) => (
                <div key={m.user_id} className="ws-row" role="listitem">
                  <div className="ws-row-main">
                    <Users aria-hidden="true" />
                    <div>
                      <div className="ws-row-title">{m.email}</div>
                      <div className="ws-row-desc">Joined {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : 'unknown'}</div>
                    </div>
                  </div>
                  {o.members.length > 1 ? (
                    <select className="ws-select" style={{ width: 'auto' }} aria-label={`Role for ${m.email}`} value={m.role}
                            onChange={(e) => changeRole(m, e.target.value)}>
                      <option value="admin">Admin</option>
                      <option value="analyst">Analyst</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : <Badge size="sm" plain>{m.role}</Badge>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Resource>
      {confirmDialog}
    </SectionCard>
  );
}

/* --------------------------------------------------------- Response targets */
const SLA_ORDER = ['Critical', 'High', 'Medium', 'Low'];
const humanHours = (h) => {
  if (!h) return '';
  if (h < 48) return `${h} ${h === 1 ? 'hour' : 'hours'}`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} days ${h % 24} hours` : `${d} days`;
};

function slaProblem(form) {
  for (const sev of SLA_ORDER) {
    const v = Number(form[sev]);
    if (!Number.isInteger(v) || v < 1 || v > 2160) return `${sev} must be a whole number of hours from 1 to 2160 (90 days).`;
  }
  for (let i = 0; i < SLA_ORDER.length - 1; i += 1) {
    if (Number(form[SLA_ORDER[i]]) > Number(form[SLA_ORDER[i + 1]])) return `${SLA_ORDER[i]} cannot have a longer target than ${SLA_ORDER[i + 1]}.`;
  }
  return null;
}

function ResponseTargetsSection() {
  const policy = useResource('/inbox/sla-policy');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (policy.data) setForm({ ...policy.data.hours }); }, [policy.data]);

  const dirty = Boolean(form && policy.data && SLA_ORDER.some((k) => Number(form[k]) !== policy.data.hours[k]));
  const problem = form ? slaProblem(form) : null;
  const isDefault = Boolean(policy.data && SLA_ORDER.every((k) => policy.data.hours[k] === DEFAULT_SLA_HOURS[k]));

  const submit = async (body) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/inbox/sla-policy`, body, { headers: authHeaders() });
      policy.setData(res.data);
      setSlaPolicyCache(res.data.hours);
      toast.success(body.reset ? 'Response targets reset to the defaults' : 'Response targets saved', { description: 'Due dates on every open threat now follow the new targets.' });
    } catch (err) {
      toast.error("Couldn't save the response targets", { description: describeError(err) });
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!problem) submit({ hours: Object.fromEntries(SLA_ORDER.map((k) => [k, Number(form[k])])) });
  };

  return (
    <SectionCard title="Response targets" subtitle="How long your team has to act on a threat once it is matched to you. Overdue counts on Home and in the threat queue follow these.">
      <Resource res={policy} label="response targets">
        {() => form && (
          <form className="ws-stack" noValidate onSubmit={onSubmit}>
            <div className="st-sla" role="group" aria-label="Hours per severity">
              {SLA_ORDER.map((sev) => (
                <label key={sev} className="st-sla-row">
                  <span><SeverityBadge severity={sev} /></span>
                  <span className="st-sla-input">
                    <Input type="number" inputMode="numeric" min={1} max={2160} step={1} value={form[sev]} aria-label={`${sev} target in hours`}
                      onChange={(e) => setForm((f) => ({ ...f, [sev]: e.target.value }))} className="ws-input-inset" data-testid={`sla-${sev}`} />
                    <span className="st-sla-unit">hours</span>
                  </span>
                  <span className="st-sla-human">{humanHours(Number(form[sev]))}{Number(form[sev]) === DEFAULT_SLA_HOURS[sev] ? ', the default' : ''}</span>
                </label>
              ))}
            </div>
            {problem && dirty && <p className="ws-error-text" role="alert">{problem}</p>}
            <p className="ws-hint">Targets count from the moment a threat is matched to your organization. Informational threats have no target. Only organization admins can change this, and every change is recorded in the audit log.</p>
            <div className="ws-form-actions">
              {!isDefault && <Button type="button" variant="ghost" disabled={saving} onClick={() => submit({ reset: true })}>Reset to defaults</Button>}
              <Button type="button" variant="ghost" disabled={!dirty || saving} onClick={() => setForm({ ...policy.data.hours })}>Discard</Button>
              <Button type="submit" disabled={!dirty || saving || Boolean(problem)} data-testid="sla-save">{saving ? 'Saving…' : 'Save targets'}</Button>
            </div>
          </form>
        )}
      </Resource>
    </SectionCard>
  );
}

/* --------------------------------------------------------------------- Page */
const SECTION_NAV = [
  { group: 'Organization', items: [
    { key: 'profile', label: 'Profile', icon: Building2, el: ProfileSection },
    { key: 'members', label: 'Members', icon: Users, el: OrganizationSection },
    { key: 'response', label: 'Response targets', icon: Timer, el: ResponseTargetsSection },
  ] },
  { group: 'Delivery', items: [
    { key: 'notifications', label: 'Notifications', icon: Bell, el: NotificationsSection },
    { key: 'integrations', label: 'Integrations', icon: Webhook, el: WebhooksSection },
  ] },
  { group: 'Your account', items: [
    { key: 'security', label: 'Security', icon: Lock, el: SecuritySection },
    { key: 'appearance', label: 'Appearance', icon: Palette, el: AppearanceSection },
  ] },
];
const SECTION_ITEMS = SECTION_NAV.flatMap((g) => g.items);
// Old tab names still reach the right section.
const SECTION_ALIASES = { organization: 'members', webhooks: 'integrations', sla: 'response' };

/**
 * Settings, one section per URL (/settings/members, /settings/response...), so a link
 * from anywhere in the product lands on the exact form.
 */
export default function Settings() {
  const { section } = useParams();
  if (section && SECTION_ALIASES[section]) return <Navigate to={`/settings/${SECTION_ALIASES[section]}`} replace />;
  const key = section || 'profile';
  const current = SECTION_ITEMS.find((i) => i.key === key);
  if (!current) return <Navigate to="/settings" replace />;
  const Section = current.el;
  return (
    <div className="pg st" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Your organization, how you are notified, and your account." />
      <div className="st-layout">
        <nav className="st-nav" aria-label="Settings sections">
          {SECTION_NAV.map((g) => (
            <div key={g.group} className="st-nav-group">
              <span className="st-nav-label">{g.group}</span>
              {g.items.map(({ key: k, label, icon: Icon }) => (
                <NavLink key={k} to={k === 'profile' ? '/settings' : `/settings/${k}`} end
                  className={`st-nav-item${k === key ? ' is-on' : ''}`} aria-current={k === key ? 'page' : undefined} data-testid={`settings-nav-${k}`}>
                  <Icon aria-hidden="true" />{label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="st-body"><Section key={key} /></div>
      </div>
    </div>
  );
}
